import { useState } from 'preact/hooks'
import type { JSX } from 'preact'
import { MEDIA, type MediaPath } from '~/generated/media'
import { media, img, srcSet, TIERS, type Tier } from '~/utils/media'

interface ImgProps {
	asset: MediaPath
	tier: Tier
	sizes: string
	alt: string
	// Cover a positioned parent that owns the box; otherwise reserve the natural aspect ratio.
	fill?: boolean
	className?: string
}

// Every raster image on the site: CDN srcset at the given tier, same-size placeholder, no reflow.
export default function Img({ asset, tier, sizes, alt, fill, className }: ImgProps) {
	const m = MEDIA[asset]
	const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading')

	const url = media(asset)
	const t = TIERS[tier]
	const set = srcSet(url, t.widths, { fit: t.fit, q: t.q }, m.w / m.h)
	const base = img(url, { w: t.widths[Math.floor(t.widths.length / 2)], fit: t.fit, q: t.q })

	const placeholder = (
		<span
			aria-hidden="true"
			className={`absolute inset-0 grid place-items-center bg-void transition-opacity duration-500 ${
				state === 'loaded' ? 'opacity-0' : 'opacity-100'
			}`}
		>
			{state === 'error' && (
				<span className="h-7 w-7 rotate-45 border border-secondary opacity-70 shadow-glow-magenta" />
			)}
		</span>
	)

	const picture = (
		<img
			src={base}
			srcSet={set}
			sizes={sizes}
			width={m.w}
			height={m.h}
			alt={alt}
			loading="lazy"
			decoding="async"
			onLoad={() => setState('loaded')}
			onError={() => setState('error')}
			className={`${fill ? 'absolute inset-0 h-full w-full' : 'block h-full w-full'} ${
				t.fit === 'cover' ? 'object-cover' : 'object-contain'
			} transition-opacity duration-700 ${state === 'loaded' ? 'opacity-100' : 'opacity-0'}`}
		/>
	)

	if (fill) {
		return (
			<>
				{picture}
				{placeholder}
			</>
		)
	}

	const style: JSX.CSSProperties = { aspectRatio: `${m.w} / ${m.h}` }
	return (
		<span className={`relative block overflow-hidden ${className ?? ''}`} style={style}>
			{picture}
			{placeholder}
		</span>
	)
}

Img.defaultProps = {
	fill: false,
	className: undefined,
}
